import { AGENT_SKILL_TITLE_MAX_CHARS } from "@common/constants/agentSkill";
import type { AgentSkill } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Link } from "@design-system/Link/Link";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import {
    KICK_DEFAULT_OUTDATED_HINT,
    KICK_DEFAULT_OUTDATED_LABEL,
    RESET_TO_DEFAULT_LABEL,
    SKILL_CHANGED_HINT,
    VIEW_KICK_DEFAULT_LABEL,
} from "./agentSkillCopy";
import {
    AgentSkillFormValues,
    agentSkillDescriptionMaxChars,
} from "./agentSkillForm";
import styles from "./AgentSkills.module.scss";

const fields = createFormikFields<AgentSkillFormValues>();

interface Props {
    skill: AgentSkill;
    isEditorOpen: boolean;
    canManage: boolean;
    defaultSkillPath: string | null;
    onReset: () => void;
    /** Set while a newer version exists than the one being edited. */
    onReloadLatest?: () => void;
}

/**
 * Title and description, as borderless fields while editing so nothing
 * moves, with the notices under them.
 */
export const AgentSkillDocumentHeader = ({
    skill,
    isEditorOpen,
    canManage,
    defaultSkillPath,
    onReset,
    onReloadLatest,
}: Props) => (
    <VStack gap={8}>
        {isEditorOpen ? (
            <fields.Text
                name="title"
                label="Title"
                labelHidden
                className={styles.titleField}
                maxLength={AGENT_SKILL_TITLE_MAX_CHARS}
            />
        ) : (
            <h1 className={styles.documentTitle}>{skill.title}</h1>
        )}
        {isEditorOpen ? (
            <fields.TextArea
                name="description"
                label="Description"
                labelHidden
                className={styles.descriptionField}
                autoGrow
                minRows={1}
                maxLength={agentSkillDescriptionMaxChars(skill.kind)}
            />
        ) : (
            <span className="text-secondary">{skill.description}</span>
        )}
        {onReloadLatest && (
            <HStack gap={8} align="center">
                <span className="text-secondary">{SKILL_CHANGED_HINT}</span>
                <Link onClick={onReloadLatest}>Reload</Link>
            </HStack>
        )}
        {skill.isDefaultOutdated && (
            <HStack gap={8} align="center" className={styles.outdatedBanner}>
                <Tag color="accent" size="sm">
                    {KICK_DEFAULT_OUTDATED_LABEL}
                </Tag>
                <span>{KICK_DEFAULT_OUTDATED_HINT}</span>
                {defaultSkillPath !== null && (
                    <Link render={<StateAwareLink to={defaultSkillPath} />}>
                        {VIEW_KICK_DEFAULT_LABEL}
                    </Link>
                )}
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!canManage}
                    onClick={onReset}
                >
                    {RESET_TO_DEFAULT_LABEL}
                </Button>
            </HStack>
        )}
    </VStack>
);
